import { useEffect, useState } from 'react';
import { api } from '../../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppDialog } from '../../../components/AppDialog';
import { AppToolbar } from '../../../components/AppToolbar';
import { useConfirm } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import { fmtLocalDate } from '../../../utils/datetime';
import { PickListDialog } from './PickListDialog';

type Tab = 'qc' | 'guide' | 'prep' | 'sampling';
const TABS: { id: Tab; title: string }[] = [
  { id: 'qc', title: 'QCTypes' }, { id: 'guide', title: 'Test Guide' },
  { id: 'prep', title: 'Prep Method' }, { id: 'sampling', title: 'Sampling Method' },
];

/** G5's Unlink confirmation (SettingFormCriteriaViewController.cs 667-678). */
const REMOVE_CONFIRM = 'You are about to remove selected record(s). Do you want to proceed?';
const yesNo = (b: unknown) => (b ? 'Yes' : 'No');
const errText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? fallback;

interface QcRow extends Record<string, unknown> { id: string; qcType: string | null; qcRole: string | null; qcRootRole: string | null; qcSource: string | null; sort: number | null; excludeReport: boolean }
interface GuideRow extends Record<string, unknown> { id: string; container: string | null; preservative: string | null; temperature: string | null; holdingTimeBeforePrep: string | null; holdingTimeBeforeAnalysis: string | null; setPrepTimeAsAnalysisTime: boolean; monitorHoldTime: boolean }
interface PrepRow extends Record<string, unknown> { oid: string; method: string | null; prepType: string | null; tier: number | null; instrument: string | null; description: string | null; sort: number | null }
interface SamplingRow extends Record<string, unknown> { oid: string; method: string | null; description: string | null; retireDate: string | null; activeDate: string | null }
interface Lookup extends Record<string, unknown> { id: string; name: string | null; number?: string | null; tier?: number | null }

const GUIDE_COLUMNS: AppColumn<GuideRow>[] = [
  { field: 'container', header: 'Container', width: 130 }, { field: 'preservative', header: 'Preservative', width: 130 },
  { field: 'temperature', header: 'Temperature', width: 110 },
  { field: 'holdingTimeBeforePrep', header: 'HoldingTime Before Prep', width: 150 },
  { field: 'holdingTimeBeforeAnalysis', header: 'HoldingTime Before Analysis', width: 160 },
  { field: 'setPrepTimeAsAnalysisTime', header: 'SetPrepTimeAsAnalysisTime', width: 170, align: 'center', value: (r) => yesNo(r.setPrepTimeAsAnalysisTime), text: (r) => yesNo(r.setPrepTimeAsAnalysisTime) },
  { field: 'monitorHoldTime', header: 'MonitorHoldTime', align: 'center', value: (r) => yesNo(r.monitorHoldTime), text: (r) => yesNo(r.monitorHoldTime) },
];
const PREP_COLUMNS: AppColumn<PrepRow>[] = [
  { field: 'method', header: 'Method', width: 140 }, { field: 'prepType', header: 'PrepType', width: 130 },
  { field: 'tier', header: 'Tier', width: 60 }, { field: 'instrument', header: 'Instrument', width: 130 },
  { field: 'description', header: 'Description', width: 180 }, { field: 'sort', header: 'Sort' },
];
const SAMPLING_COLUMNS: AppColumn<SamplingRow>[] = [
  { field: 'method', header: 'Method', width: 150 }, { field: 'description', header: 'Description', width: 220 },
  { field: 'retireDate', header: 'RetireDate', width: 110, text: (r) => (r.retireDate ? fmtLocalDate(r.retireDate) : '') },
  { field: 'activeDate', header: 'ActiveDate', text: (r) => (r.activeDate ? fmtLocalDate(r.activeDate) : '') },
];

/**
 * G5 "Method Defaults": QCTypes (Add · Remove — G5 captions them ADD / REMOVE; G6 uses the standard title case, owner 2026-10-15 — Exclude Report tick) · Test Guide (New · Delete)
 * · Prep Method (New · Delete) · Sampling Method (Add · Remove). Each change takes effect at
 * once, as in G5. `onQcTypesChanged` lets the popup refresh the QC Sample Parameters dropdown.
 */
export function MethodDefaults({ testId, onQcTypesChanged }: { testId: string; onQcTypesChanged: () => void }) {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const [tab, setTab] = useState<Tab>('qc');
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<'qcAdd' | 'samplingAdd' | 'guideNew' | 'prepNew' | null>(null);
  const base = `/api/v1/test-methods/${testId}`;

  const urlOf = (t: Tab) => ({ qc: `${base}/qc-types`, guide: `${base}/test-guide`, prep: `${base}/prep-methods`, sampling: `${base}/sampling-methods` })[t];
  const idOf = (r: Record<string, unknown>) => String(r.id ?? r.oid);

  const load = async (t: Tab = tab) => {
    setLoading(true);
    try {
      const res = await api.get<{ data?: Record<string, unknown>[] }>(urlOf(t));
      setRows(res.data?.data ?? []);
      setSelected(new Set());
    } catch { notify.error('The list could not be loaded.'); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(tab); }, [tab, testId]);

  const ids = () => [...selected].map(String);
  const askRemove = async () => {
    if (selected.size === 0) { notify.info('Please select an item to proceed further.'); return false; }
    return confirm({ title: 'Remove', tone: 'danger', confirmLabel: 'Yes', message: REMOVE_CONFIRM });
  };

  const run = async (work: () => Promise<unknown>, ok: string, fail: string, after?: () => void) => {
    try { await work(); notify.success(ok); after?.(); }
    catch (e) { notify.error(errText(e, fail)); }
    await load();
  };

  const removeSelected = async () => {
    if (!(await askRemove())) return;
    if (tab === 'qc') await run(() => api.post(`${base}/qc-types/remove`, { ids: ids() }), 'Removed successfully.', 'The QC types could not be removed.', onQcTypesChanged);
    if (tab === 'sampling') await run(() => api.post(`${base}/sampling-methods/remove`, { ids: ids() }), 'Removed successfully.', 'The sampling methods could not be removed.');
    if (tab === 'guide') await run(async () => { for (const id of ids()) await api.delete(`${base}/test-guide/${id}`); }, 'Deleted successfully.', 'The test guide could not be deleted.');
    if (tab === 'prep') await run(async () => { for (const id of ids()) await api.delete(`${base}/prep-methods/${id}`); }, 'Deleted successfully.', 'The prep method could not be deleted.');
  };

  const setExclude = async (r: QcRow, value: boolean) => {
    try {
      await api.patch(`${base}/qc-types/${r.id}/exclude-report`, { excludeReport: value });
      setRows((d) => d.map((x) => (x.id === r.id ? { ...x, excludeReport: value } : x)));
    } catch (e) { notify.error(errText(e, 'Exclude Report could not be saved.')); }
  };

  const qcColumns: AppColumn<QcRow>[] = [
    { field: 'qcType', header: 'QCType', width: 130 }, { field: 'qcRole', header: 'QcRole', width: 110 },
    { field: 'qcRootRole', header: 'QCRootRole', width: 110 }, { field: 'qcSource', header: 'QC_Source', width: 110 },
    { field: 'sort', header: 'Sort', width: 70 },
    { field: 'excludeReport', header: 'Exclude Report', align: 'center', value: (r) => yesNo(r.excludeReport), text: (r) => yesNo(r.excludeReport),
      render: (r) => (
        <input type="checkbox" className="sdt-check" checked={r.excludeReport} aria-label={`Exclude Report — ${r.qcType}`}
          onClick={(e) => e.stopPropagation()} onChange={(e) => { void setExclude(r, e.target.checked); }} />
      ) },
  ];

  const columns = (tab === 'qc' ? qcColumns : tab === 'guide' ? GUIDE_COLUMNS : tab === 'prep' ? PREP_COLUMNS : SAMPLING_COLUMNS) as AppColumn<Record<string, unknown>>[];
  // QCTypes / Sampling Method link and unlink (G5 renames them ADD / REMOVE); Test Guide / Prep Method create and delete.
  const linkTab = tab === 'qc' || tab === 'sampling';

  return (
    <div className="tmd-section">
      <h3 className="tmv-h">Method Defaults</h3>
      <div className="tmv-tabs" role="tablist" aria-label="Method Defaults">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={tab === t.id ? 'on' : ''} onClick={() => setTab(t.id)}>{t.title}</button>
        ))}
      </div>
      <div className="tmv-panel">
        <AppToolbar
          noHotkeys showMeta={false} crudFirst
          newLabel={linkTab ? 'Add' : 'New'}
          onNew={() => setDialog(tab === 'qc' ? 'qcAdd' : tab === 'sampling' ? 'samplingAdd' : tab === 'guide' ? 'guideNew' : 'prepNew')}
          deleteLabel={linkTab ? 'Remove' : 'Delete'}
          onDelete={() => { void removeSelected(); }}
          exportData={{ filename: TABS.find((t) => t.id === tab)!.title, ...gridExportMatrix(rows, columns) }}
        />
        <AppDataGrid<Record<string, unknown>>
          rows={rows} columns={columns} getRowId={idOf} loading={loading}
          selectable selectedIds={selected} onSelectionChange={setSelected}
          defaultPageSize={10} hideHeaderTools emptyText="No data to display"
        />
      </div>

      {dialog === 'qcAdd' && (
        <PickListDialog<Lookup> title="QC Type" url={`${base}/qc-types/available`}
          columns={[{ field: 'name', header: 'QCType' }]} getId={(r) => r.id}
          onClose={() => setDialog(null)}
          onPicked={(ps) => { setDialog(null); void run(() => api.post(`${base}/qc-types/add`, { ids: ps.map((p) => p.id) }), 'Added successfully.', 'The QC types could not be added.', onQcTypesChanged); }} />
      )}
      {dialog === 'samplingAdd' && (
        <PickListDialog<SamplingRow> title="Method" url={`${base}/sampling-methods/available`}
          columns={SAMPLING_COLUMNS} getId={(r) => r.oid}
          onClose={() => setDialog(null)}
          onPicked={(ps) => { setDialog(null); void run(() => api.post(`${base}/sampling-methods/add`, { ids: ps.map((p) => p.oid) }), 'Added successfully.', 'The sampling methods could not be added.'); }} />
      )}
      {dialog === 'guideNew' && <TestGuideDialog base={base} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); void load(); }} />}
      {dialog === 'prepNew' && <PrepMethodDialog base={base} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); void load(); }} />}
    </div>
  );
}

function useLookup(url: string): Lookup[] {
  const [opts, setOpts] = useState<Lookup[]>([]);
  useEffect(() => {
    let alive = true;
    api.get<{ data?: Lookup[] }>(url).then(({ data }) => { if (alive) setOpts(data.data ?? []); }).catch(() => undefined);
    return () => { alive = false; };
  }, [url]);
  return opts;
}

function Select({ label, required, value, onChange, options, show = (o) => o.name ?? '' }: {
  label: string; required?: boolean; value: string; onChange: (v: string) => void; options: Lookup[]; show?: (o: Lookup) => string;
}) {
  return (
    <>
      <label>{label}{required && <span className="req">*</span>}</label>
      <select className="sd-input" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{required ? '— Select —' : 'N/A'}</option>
        {options.map((o) => <option key={o.id} value={o.id}>{show(o)}</option>)}
      </select>
    </>
  );
}

/** G5 TestGuide_DetailView: Container* · Preservative* · Temperature · holding times · SetPrepTimeAsAnalysisTime
 *  (ticking it copies the prep holding time into the analysis one; unticking clears it — G5 1806-1820). */
function TestGuideDialog({ base, onClose, onSaved }: { base: string; onClose: () => void; onSaved: () => void }) {
  const notify = useNotify();
  const containers = useLookup('/api/v1/test-methods/lookups/container');
  const preservatives = useLookup('/api/v1/test-methods/lookups/preservative');
  const temperatures = useLookup('/api/v1/test-methods/lookups/temperature');
  const holding = useLookup('/api/v1/test-methods/lookups/holding-times');
  const [f, setF] = useState({ containerId: '', preservativeId: '', temperatureId: '', prepId: '', analysisId: '', same: false });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.containerId) { notify.warning('Container should not be empty.'); return; }
    if (!f.preservativeId) { notify.warning('Preservative should not be empty.'); return; }
    setSaving(true);
    try {
      await api.post(`${base}/test-guide`, {
        containerId: f.containerId, preservativeId: f.preservativeId, temperatureId: f.temperatureId || null,
        holdingTimeBeforePrepId: f.prepId || null, holdingTimeBeforeAnalysisId: f.analysisId || null, setPrepTimeAsAnalysisTime: f.same,
      });
      notify.success('Saved successfully.');
      onSaved();
    } catch (e) { notify.error(errText(e, 'The test guide could not be saved.')); }
    finally { setSaving(false); }
  };
  return (
    <AppDialog open title="Test Guide" width={520} className="dlg-center tmd-24" onClose={onClose}
      footer={<>
        <button type="button" className="ca-btn ca-btn-solid" disabled={saving} onClick={() => { void save(); }}>OK</button>
        <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
      </>}>
      <div className="tmd-form">
        <Select label="Container" required value={f.containerId} onChange={(v) => setF({ ...f, containerId: v })} options={containers} />
        <Select label="Preservative" required value={f.preservativeId} onChange={(v) => setF({ ...f, preservativeId: v })} options={preservatives} />
        <Select label="Temperature" value={f.temperatureId} onChange={(v) => setF({ ...f, temperatureId: v })} options={temperatures} />
        <Select label="HoldingTime Before Prep" value={f.prepId}
          onChange={(v) => setF({ ...f, prepId: v, analysisId: f.same ? v : f.analysisId })} options={holding} />
        <label>SetPrepTimeAsAnalysisTime</label>
        <input type="checkbox" checked={f.same}
          onChange={(e) => setF({ ...f, same: e.target.checked, analysisId: e.target.checked ? f.prepId : '' })} />
        <Select label="HoldingTime Before Analysis" value={f.analysisId} onChange={(v) => setF({ ...f, analysisId: v })} options={holding} />
      </div>
    </AppDialog>
  );
}

/** G5 PrepMethod_DetailView: Method* · Prep Type* (its Tier) · Instrument · Description. */
function PrepMethodDialog({ base, onClose, onSaved }: { base: string; onClose: () => void; onSaved: () => void }) {
  const notify = useNotify();
  const methods = useLookup('/api/v1/test-methods/methods');
  const prepTypes = useLookup('/api/v1/test-methods/prep-types');
  const [f, setF] = useState({ methodId: '', prepTypeId: '', instrument: '', description: '' });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`${base}/prep-methods`, {
        methodId: f.methodId || null, prepTypeId: f.prepTypeId || null, instrument: f.instrument || null, description: f.description || null,
      });
      notify.success('Saved successfully.');
      onSaved();
    } catch (e) { notify.error(errText(e, 'The prep method could not be saved.')); }
    finally { setSaving(false); }
  };
  return (
    <AppDialog open title="Prep Method" width={520} className="dlg-center tmd-24" onClose={onClose}
      footer={<>
        <button type="button" className="ca-btn ca-btn-solid" disabled={saving} onClick={() => { void save(); }}>OK</button>
        <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
      </>}>
      <div className="tmd-form">
        <Select label="Method" required value={f.methodId} onChange={(v) => setF({ ...f, methodId: v })} options={methods}
          show={(o) => o.number || o.name || ''} />
        <Select label="Prep Type" required value={f.prepTypeId} onChange={(v) => setF({ ...f, prepTypeId: v })} options={prepTypes}
          show={(o) => `${o.name ?? ''}${o.tier != null ? ` (Tier ${o.tier})` : ''}`} />
        <label>Instrument</label>
        <input className="sd-input" value={f.instrument} onChange={(e) => setF({ ...f, instrument: e.target.value })} />
        <label>Description</label>
        <input className="sd-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
      </div>
    </AppDialog>
  );
}
